import { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import BackIcon from '@mui/icons-material/ChevronLeft'

type FlyoutSurfaceProps = {
  /** The element the desktop popover anchors to; also drives the open state. */
  readonly anchorElement: HTMLElement | null
  readonly onClose: () => void
  readonly children: ReactNode
}

/**
 * The host the app-menu flyout body renders into. The body is identical on both
 * surfaces; only the host differs - an anchored popover on desktop, a
 * full-screen dialog (with a back chevron to leave without choosing a
 * destination) below smxl.
 */
const FlyoutSurface = ({
  anchorElement,
  onClose,
  children,
}: FlyoutSurfaceProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('smxl'))

  if (isMobile) {
    return (
      <Dialog
        fullScreen
        open={Boolean(anchorElement)}
        onClose={onClose}
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}
      >
        {/* Back chevron header: closes the flyout when the user backs out
            without picking a destination (a link tap navigates + closes too). */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          padding={1}
          flexShrink={0}
        >
          <IconButton onClick={onClose} aria-label="Close profile menu">
            <BackIcon />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600}>
            Back
          </Typography>
        </Box>
        <Divider />
        {/* minHeight 0 lets this flex child shrink so its own scroll engages
            instead of the dialog paper overflowing. */}
        <Box flex={1} minHeight={0} overflow="auto">
          {children}
        </Box>
      </Dialog>
    )
  }

  return (
    <Popover
      anchorEl={anchorElement}
      open={Boolean(anchorElement)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      // Without this, the underlying MUI Modal sets `overflow: hidden` on the
      // body while open, which on Windows removes the always-present scrollbar
      // (`body { overflow-y: scroll }`) and shifts the layout. Scroll locking
      // isn't needed here: the popover anchors to the fixed topbar, so page
      // scrolling can't move it out of alignment.
      disableScrollLock
      slotProps={{ paper: { sx: { width: 340, marginTop: 1 } } }}
    >
      {children}
    </Popover>
  )
}

export default FlyoutSurface
